<template>
  <div class="modal fade" id="newInteractionModal" tabindex="-1" 
       :class="{ show: show }" 
       :style="{ display: show ? 'block' : 'none' }">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ translate('interactions.new', 'New Interaction') }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <!-- Phone Number with prefix dropdown -->
            <div class="mb-3">
              <label class="form-label">{{ translate('interactions.phone_number', 'Phone Number') }}</label>
              <div class="position-relative">
                <div class="input-group">
                  <select
                    class="form-select prefix-select"
                    v-model="selectedPrefix"
                    :disabled="isDemo"
                    aria-label="Country code"
                  >
                    <option v-for="p in countryPrefixOptions" :key="p.code" :value="p.code">
                      {{ p.flag }} {{ p.label }}
                    </option>
                  </select>
                  <input
                    type="tel"
                    class="form-control"
                    v-model="form.phone"
                    :maxlength="phoneDigits"
                    inputmode="numeric"
                    :placeholder="translate('interactions.phone_placeholder', 'Local number')"
                    @input="handlePhoneInput"
                    @paste.prevent="handlePhonePaste"
                    required
                    :disabled="isDemo"
                    :class="{ 'bg-light': isDemo, 'is-invalid': phoneError }"
                  >
                </div>
                <div v-if="suggestions.phones.length && showSuggestions.phone && !isDemo"
                     class="suggestions-list">
                  <button
                    v-for="suggestion in suggestions.phones"
                    :key="suggestion.phone"
                    type="button"
                    class="suggestion-item"
                    @click="selectPhone(suggestion)"
                  >
                    {{ suggestion.phone }} - {{ suggestion.name }}
                  </button>
                </div>
              </div>
              <div v-if="phoneError" class="invalid-feedback d-block">
                <i class="bi bi-exclamation-circle me-1"></i>{{ phoneError }}
              </div>
              <div class="form-text text-muted mt-1">
                <i class="bi bi-info-circle me-1"></i>
                {{ selectedPrefixLabel }} — {{ translate('interactions.local_number_only', 'enter local number without country code') }}
              </div>
              <!-- Info for demo accounts -->
              <div v-if="isDemo" class="form-text text-warning mt-1">
                <i class="bi bi-info-circle me-1"></i>
                {{ translate('interactions.demo_phone_info', 'This message is covered by your free credit. To add more interactions please purchase credits.') }}
              </div>
            </div>

            <!-- Customer Name with suggestions -->
            <div class="mb-3">
              <label class="form-label">{{ translate('interactions.customer_name', 'Customer Name') }}</label>
              <div class="position-relative">
                <input
                  type="text"
                  class="form-control"
                  v-model="form.customerName"
                  @input="handleNameInput"
                  required
                >
                <div v-if="suggestions.names.length && showSuggestions.name" 
                     class="suggestions-list">
                  <button
                    v-for="suggestion in suggestions.names"
                    :key="suggestion.id"
                    type="button"
                    class="suggestion-item"
                    @click="selectCustomer(suggestion)"
                  >
                    {{ suggestion.name }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Pet Name with suggestions -->
            <div class="mb-3">
              <label class="form-label">{{ translate('interactions.pet_name_optional', 'Pet Name') }}</label>
              <div class="position-relative">
                <input
                  type="text"
                  class="form-control"
                  v-model="form.petName"
                  @input="handlePetInput"
                >
                <div v-if="suggestions.pets.length && showSuggestions.pet" 
                     class="suggestions-list">
                  <button
                    v-for="suggestion in suggestions.pets"
                    :key="suggestion"
                    type="button"
                    class="suggestion-item"
                    @click="selectPet(suggestion)"
                  >
                    {{ suggestion }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Service -->
            <div class="mb-3">
              <label class="form-label">{{ translate('interactions.service', 'Service') }}</label>
              <select class="form-select" v-model="form.service" required>
                <option value="" selected disabled>{{ translate('interactions.select_service', 'Select a service') }}</option>
                <option value="Grooming">{{ translate('interactions.grooming', 'Grooming') }}</option>
                <option value="Retail">{{ translate('interactions.retail', 'Retail') }}</option>
                <option value="Vet">{{ translate('interactions.vet', 'Vet') }}</option>
              </select>
            </div>

            <!-- Service Date and Time -->
            <div class="mb-3">
              <label class="form-label">{{ translate('interactions.service_date_time', 'Service Date and Time') }}</label>
              <div class="datetime-wrapper" @click="openDatePicker">
                <input
                  ref="dateTimeInput"
                  type="datetime-local"
                  class="form-control no-keyboard"
                  v-model="form.serviceDateTime"
                  :min="minDate"
                  :max="maxDate"
                  required
                >
              </div>
            </div>

            <div class="text-end">
              <button type="button" class="btn btn-secondary me-2" @click="closeModal">
                {{ translate('messages.cancel', 'Cancel') }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                {{ translate('interactions.save', 'Save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { useStore } from 'vuex'
import { Modal } from 'bootstrap'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'

export default {
  name: 'NewInteractionModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:show', 'interaction-created'],
  setup(props, { emit }) {
    const store = useStore()
    const loading = ref(false)
    let modal = null
    const dateTimeInput = ref(null)
    const isDemo = ref(false)
    const userPhoneNumber = ref('')

    const countryPrefixOptions = [
      { code: '57', label: '+57', flag: '🇨🇴' },
      { code: '1',  label: '+1',  flag: '🇺🇸' },
      { code: '52', label: '+52', flag: '🇲🇽' },
      { code: '34', label: '+34', flag: '🇪🇸' },
      { code: '44', label: '+44', flag: '🇬🇧' },
    ]

    const PHONE_COUNTRY_CONFIG = {
      '57': { digits: 10, example: '3001234567' },
      '1':  { digits: 10, example: '2025551234' },
      '52': { digits: 10, example: '5512345678' },
      '34': { digits: 9,  example: '612345678'  },
      '44': { digits: 10, example: '7911123456' },
    }

    const phoneError = ref('')

    // Default prefix based on user dashboard language
    const selectedPrefix = ref(store.state.language === 'es' ? '57' : '1')

    // Human-readable label for the hint text
    const selectedPrefixLabel = computed(() => {
      const match = countryPrefixOptions.find(p => p.code === selectedPrefix.value)
      return match ? `${match.flag} ${match.label}` : `+${selectedPrefix.value}`
    })

    const phoneDigits = computed(() => PHONE_COUNTRY_CONFIG[selectedPrefix.value]?.digits ?? 10)
    const phoneExample = computed(() => PHONE_COUNTRY_CONFIG[selectedPrefix.value]?.example ?? '')

    // Keep prefix in sync if user switches language elsewhere
    watch(() => store.state.language, (lang) => {
      selectedPrefix.value = lang === 'es' ? '57' : '1'
    })

    // Re-validate when prefix changes with an existing phone value
    watch(selectedPrefix, () => {
      if (form.value.phone) {
        const digits = form.value.phone.replace(/\D/g, '')
        phoneError.value = digits.length !== phoneDigits.value
          ? `Requires exactly ${phoneDigits.value} digits (e.g. ${phoneExample.value})`
          : ''
      } else {
        phoneError.value = ''
      }
    })

    onMounted(() => {
      modal = new Modal(document.getElementById('newInteractionModal'))

      // Listen to bootstrap modal events
      const modalEl = document.getElementById('newInteractionModal')
      modalEl.addEventListener('hidden.bs.modal', () => {
        emit('update:show', false)
      })

      // Set default datetime to current LOCAL time
      form.value.serviceDateTime = toLocalDT(new Date())

      // if the account is in demo mode, use the user's phone number
      checkDemoStatus()
    })

    // Watch for show prop changes
    watch(() => props.show, (newVal) => {
      if (newVal && modal) {
        form.value.serviceDateTime = toLocalDT(new Date())
        updateDateLimits()
        modal.show()
        // check demo status when modal opens
        checkDemoStatus()
      } else if (modal) {
        modal.hide()
      }
    })

    // Function to check if the account is in demo mode and configure the form accordingly
    const checkDemoStatus = async () => {
      // Get the demo status from the store
      isDemo.value = store.state.demo === true || store.state.demo === 1

      // If we are in demo mode, configure the phone number with the user's current phone number
      if (isDemo.value) {
        // Get the user's current phone number from the store
        const user = store.state.user
        if (user) {
          try {
            // Search for the user's document in Firestore to get their phone number
            const userRef = doc(db, 'users', user.uid)
            const userDoc = await getDoc(userRef)

            if (userDoc.exists()) {
              const userData = userDoc.data()
              if (userData && userData.phoneNumber) {
                userPhoneNumber.value = userData.phoneNumber
                // Pre-fill and disable the phone number field
                form.value.phone = userPhoneNumber.value
              }
            }
          } catch (error) {
            console.error('Error al obtener datos del usuario:', error)
          }
        }
      }
    }

    const form = ref({
      phone: '',
      customerName: '',
      petName: '',
      service: '',
      serviceDateTime: '',
      rating: 5
    })

    const suggestions = ref({
      phones: [],
      names: [],
      pets: []
    })

    const showSuggestions = ref({
      phone: false,
      name: false,
      pet: false
    })

    const minDate = ref('')
    const maxDate = ref('')

    // Format a Date as YYYY-MM-DDTHH:MM in the user's LOCAL timezone
    const toLocalDT = (date) => {
      const offset = date.getTimezoneOffset()
      const local = new Date(date.getTime() - offset * 60 * 1000)
      return local.toISOString().slice(0, 16)
    }

    const updateDateLimits = () => {
      // Set max date as today
      const today = new Date()
      maxDate.value = toLocalDT(today)

      // Set min date as 5 days ago
      const fiveDaysAgo = new Date()
      fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5)
      minDate.value = toLocalDT(fiveDaysAgo)

      // Ensure current value is within range
      const currentDate = new Date(form.value.serviceDateTime)
      if (currentDate < fiveDaysAgo) {
        form.value.serviceDateTime = minDate.value
      } else if (currentDate > today) {
        form.value.serviceDateTime = maxDate.value
      }
    }

    // Update date limits when component is mounted
    onMounted(() => {
      updateDateLimits()
    })

    const handlePhoneInput = async () => {
      // Strip non-digits (handles manual typing)
      form.value.phone = form.value.phone.replace(/\D/g, '')
      const phone = form.value.phone
      const required = phoneDigits.value

      if (phone.length > 0 && phone.length !== required) {
        phoneError.value = `Requires exactly ${required} digits (e.g. ${phoneExample.value})`
      } else {
        phoneError.value = ''
      }

      if (phone.length > 0) {
        const searchPhone = selectedPrefix.value + phone
        suggestions.value.phones = await store.dispatch('searchCustomersByPhone', searchPhone)
        showSuggestions.value.phone = true
      } else {
        suggestions.value.phones = []
        showSuggestions.value.phone = false
      }
    }

    const handlePhonePaste = async (event) => {
      const text = event.clipboardData?.getData('text') ?? ''
      let stripped = text.replace(/\D/g, '')
      const required = phoneDigits.value

      // Trim to last N digits so +57 300 2799209 → 3002799209
      if (stripped.length > required) {
        stripped = stripped.slice(-required)
      }
      form.value.phone = stripped

      phoneError.value = stripped.length > 0 && stripped.length !== required
        ? `Requires exactly ${required} digits (e.g. ${phoneExample.value})`
        : ''

      if (stripped.length > 0) {
        const searchPhone = selectedPrefix.value + stripped
        suggestions.value.phones = await store.dispatch('searchCustomersByPhone', searchPhone)
        showSuggestions.value.phone = true
      } else {
        suggestions.value.phones = []
        showSuggestions.value.phone = false
      }
    }

    const handleNameInput = async () => {
      form.value.customerName = form.value.customerName.toUpperCase()
      const name = form.value.customerName.trim()
      if (name.length > 0) {
        suggestions.value.names = await store.dispatch('searchCustomersByName', name)
        showSuggestions.value.name = true
      } else {
        suggestions.value.names = []
        showSuggestions.value.name = false
      }
    }

    const handlePetInput = async () => {
      form.value.petName = form.value.petName.toUpperCase()
      const pet = form.value.petName.trim()
      if (pet.length > 0) {
        suggestions.value.pets = await store.dispatch('searchPetsByName', pet)
        showSuggestions.value.pet = true
      } else {
        suggestions.value.pets = []
        showSuggestions.value.pet = false
      }
    }

    // Strip country prefix from a phone string and set the dropdown accordingly
    const stripPrefix = (rawPhone) => {
      let phone = rawPhone.replace('whatsapp:', '').replace('+', '').trim()
      for (const p of countryPrefixOptions) {
        if (phone.startsWith(p.code)) {
          selectedPrefix.value = p.code
          return phone.slice(p.code.length)
        }
      }
      return phone
    }

    const selectPhone = (customer) => {
      form.value.phone = stripPrefix(customer.phone)
      phoneError.value = ''
      form.value.customerName = (customer.name || '').toUpperCase()
      if (customer.pets && customer.pets.length > 0) {
        form.value.petName = (customer.pets[0] || '').toUpperCase()
      }
      showSuggestions.value.phone = false
    }

    const selectCustomer = (customer) => {
      form.value.customerName = (customer.name || '').toUpperCase()
      form.value.phone = stripPrefix(customer.phone)
      phoneError.value = ''
      if (customer.pets && customer.pets.length > 0) {
        form.value.petName = (customer.pets[0] || '').toUpperCase()
      }
      showSuggestions.value.name = false
    }

    const selectPet = (petName) => {
      form.value.petName = (petName || '').toUpperCase()
      showSuggestions.value.pet = false
    }

    const handleSubmit = async () => {
      try {
        loading.value = true

        // Validate phone digit count before submitting
        const digits = form.value.phone.replace(/\D/g, '')
        if (digits.length !== phoneDigits.value) {
          phoneError.value = `Requires exactly ${phoneDigits.value} digits (e.g. ${phoneExample.value})`
          loading.value = false
          return
        }

        // Ensure we have a valid date
        if (!form.value.serviceDateTime) {
          form.value.serviceDateTime = toLocalDT(new Date())
        }

        // Combine selected country prefix with local digits for a full number (e.g. 573161181366)
        const fullPhone = selectedPrefix.value + digits

        const interactionData = {
          phone: fullPhone,
          customerName: form.value.customerName,
          petName: form.value.petName || '',
          service: form.value.service || '',
          date: new Date().toISOString(),
          serviceDateTime: new Date(form.value.serviceDateTime).toISOString(),
          rating: form.value.rating || 5
        }

        await store.dispatch('createInteraction', interactionData)

        // Update the list of customers to ensure names are displayed correctly
        await store.dispatch('fetchCustomers')

        emit('interaction-created')
        closeModal()

      } catch (error) {
        console.error('Error creating interaction:', error)
      } finally {
        loading.value = false
      }
    }

    const closeModal = () => {
      if (modal) {
        modal.hide()
        // Remove backdrop manually
        const backdrop = document.querySelector('.modal-backdrop')
        if (backdrop) {
          backdrop.remove()
        }
        document.body.classList.remove('modal-open')
      }
      emit('update:show', false)
      form.value = {
        phone: '',
        customerName: '',
        petName: '',
        service: '',
        serviceDateTime: toLocalDT(new Date()),
        rating: 5
      }
      phoneError.value = ''
      selectedPrefix.value = store.state.language === 'es' ? '57' : '1'
      Object.keys(showSuggestions.value).forEach(key => {
        showSuggestions.value[key] = false
      })
    }

    const handleServiceInput = () => {
      // Add your logic here
    }

    const openDatePicker = () => {
      if (dateTimeInput.value) {
        dateTimeInput.value.showPicker()
      }
    }

    const translate = (key, fallback) => {
      return store.getters.translate(key, fallback)
    }

    return {
      form,
      loading,
      suggestions,
      showSuggestions,
      isDemo,
      userPhoneNumber,
      countryPrefixOptions,
      selectedPrefix,
      selectedPrefixLabel,
      handlePhoneInput,
      handlePhonePaste,
      handleNameInput,
      handlePetInput,
      selectPhone,
      selectCustomer,
      selectPet,
      handleSubmit,
      closeModal,
      handleServiceInput,
      openDatePicker,
      dateTimeInput,
      minDate,
      maxDate,
      phoneDigits,
      phoneExample,
      phoneError,
      translate
    }
  }
}
</script>

<style scoped>
.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.suggestion-item {
  display: block;
  width: 100%;
  padding: 0.5rem;
  text-align: left;
  border: none;
  background: none;
}

.suggestion-item:hover {
  background-color: #f8f9fa;
}

.datetime-wrapper {
  position: relative;
  cursor: pointer;
}

.datetime-wrapper input {
  cursor: pointer;
}

/* Remove default calendar icon */
.datetime-wrapper input::-webkit-calendar-picker-indicator {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

/* Prevent manual input on datetime field */
.no-keyboard {
  pointer-events: none;
}
.no-keyboard::-webkit-calendar-picker-indicator {
  pointer-events: all;
}

.prefix-select {
  max-width: 120px;
  flex-shrink: 0;
}
</style>